import { useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  List,
  ListItem,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import type {
  PatientResolution,
  SessionPatientMatchSchema,
  SessionPatientSchema,
} from '../store/hooks';

/** Sentinel Select value for "create a new patient" (vs. linking to an id). */
const CREATE_NEW = '__create_new__';

interface PatientSelectionPanelProps {
  patients: SessionPatientSchema[];
  /** 'all' commits every staged patient; 'specific' reveals the checkboxes. */
  scope: 'all' | 'specific';
  onScopeChange: (scope: 'all' | 'specific') => void;
  /** Patient keys the operator has CHECKED (only meaningful in 'specific'). */
  selected: Set<string>;
  /** True when every listed patient is checked (drives the select-all box). */
  allChecked: boolean;
  onToggle: (patientKey: string) => void;
  onToggleAll: () => void;
  isLoading?: boolean;
  /** Per-patient match outcome, keyed by patient_key (staged patients only). */
  matchByKey: Map<string, SessionPatientMatchSchema>;
  /** Operator match decisions, keyed by patient_key. */
  resolutions: Map<string, PatientResolution>;
  /** Set (or clear, with null) a patient's match resolution. */
  onResolve: (patientKey: string, resolution: PatientResolution | null) => void;
}

/** "3 encounter · 3 note · 1 patient_coverage" — what's attached to a patient. */
function describeCounts(counts: SessionPatientSchema['counts']): string {
  const entries = Object.entries(counts ?? {});
  if (entries.length === 0) return 'no attached records';
  return entries.map(([type, n]) => `${n} ${type}`).join(' · ');
}

type Status =
  | 'matched'
  | 'ambiguous'
  | 'new'
  | 'invalid'
  | 'committed'
  | undefined;

const STATUS_CHIP: Record<
  NonNullable<Status>,
  { label: string; color: 'success' | 'warning' | 'info' | 'default' | 'error' }
> = {
  matched: { label: 'Matched', color: 'success' },
  ambiguous: { label: 'Ambiguous', color: 'warning' },
  new: { label: 'New', color: 'info' },
  invalid: { label: 'Invalid', color: 'error' },
  committed: { label: 'Committed', color: 'default' },
};

/**
 * Patient scope + match resolution for a commit. Each staged patient shows how
 * it would resolve against existing patients (Matched / Ambiguous / New); the
 * operator can link to a chosen existing patient or create a new one. Ambiguous
 * patients must be resolved before commit (the parent gates the button). The
 * all/specific toggle controls which patients commit; the checkbox is the
 * include/skip control in 'specific' mode.
 */
export function PatientSelectionPanel({
  patients,
  scope,
  onScopeChange,
  selected,
  allChecked,
  onToggle,
  onToggleAll,
  isLoading = false,
  matchByKey,
  resolutions,
  onResolve,
}: PatientSelectionPanelProps) {
  if (!isLoading && patients.length === 0) return null;

  const selectedCount = selected.size;
  const specific = scope === 'specific';

  const statusOf = (p: SessionPatientSchema): Status => {
    const match = matchByKey.get(p.patient_key);
    if (match) return match.status as Status;
    return p.patient_staged ? undefined : 'committed';
  };

  /** Current Select value: explicit resolution, else the auto match (matched). */
  const selectValue = (p: SessionPatientSchema): string => {
    const res = resolutions.get(p.patient_key);
    if (res) return res.action === 'create' ? CREATE_NEW : res.target_patient_id ?? '';
    const match = matchByKey.get(p.patient_key);
    if (match?.status === 'matched' && match.candidates?.[0]) {
      return match.candidates[0].patient_id; // auto target, not yet overridden
    }
    return '';
  };

  const handleSelect = (patientKey: string, value: string) => {
    if (value === CREATE_NEW) onResolve(patientKey, { action: 'create' });
    else onResolve(patientKey, { action: 'link', target_patient_id: value });
  };

  // View-only status filter (does not change what commits). Count by status,
  // then show a filter chip per present status; 'all' clears the filter.
  const [statusFilter, setStatusFilter] = useState<NonNullable<Status> | 'all'>(
    'all',
  );
  const FILTER_ORDER: NonNullable<Status>[] = [
    'matched',
    'ambiguous',
    'new',
    'invalid',
    'committed',
  ];
  const counts: Partial<Record<NonNullable<Status>, number>> = {};
  for (const p of patients) {
    const s = statusOf(p);
    if (s) counts[s] = (counts[s] ?? 0) + 1;
  }
  const presentStatuses = FILTER_ORDER.filter(s => counts[s]);
  const visiblePatients =
    statusFilter === 'all'
      ? patients
      : patients.filter(p => statusOf(p) === statusFilter);

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Patients
      </Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={scope}
        onChange={(_e, next) => {
          if (next) onScopeChange(next as 'all' | 'specific');
        }}
        sx={{ mb: 1 }}
      >
        <ToggleButton value="all">All patients</ToggleButton>
        <ToggleButton value="specific">Specific patients</ToggleButton>
      </ToggleButtonGroup>

      {specific && (
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={allChecked}
              indeterminate={!allChecked && selectedCount > 0}
              disabled={patients.length === 0}
              onChange={onToggleAll}
              slotProps={{ input: { 'aria-label': 'Select all patients' } }}
            />
          }
          label={
            <Typography variant="body2">
              {selectedCount}/{patients.length} selected
            </Typography>
          }
        />
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {specific
          ? 'Each checked patient is committed with everything attached to them; the rest are left staged.'
          : 'Every staged patient below will be committed. Resolve any ambiguous matches first.'}
      </Typography>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
            <Chip
              size="small"
              label={`All ${patients.length}`}
              variant={statusFilter === 'all' ? 'filled' : 'outlined'}
              onClick={() => setStatusFilter('all')}
            />
            {presentStatuses.map(s => (
              <Chip
                key={s}
                size="small"
                label={`${STATUS_CHIP[s].label} ${counts[s]}`}
                color={STATUS_CHIP[s].color}
                variant={statusFilter === s ? 'filled' : 'outlined'}
                onClick={() => setStatusFilter(s)}
              />
            ))}
          </Box>
          <List dense sx={{ maxHeight: 360, overflow: 'auto' }}>
            {visiblePatients.map(p => {
            const status = statusOf(p);
            const match = matchByKey.get(p.patient_key);
            const resolvable = status === 'matched' || status === 'ambiguous';
            const chip = status ? STATUS_CHIP[status] : undefined;
            return (
              <ListItem key={p.patient_key} disableGutters sx={{ gap: 1 }}>
                {specific && (
                  <Checkbox
                    size="small"
                    checked={selected.has(p.patient_key)}
                    onChange={() => onToggle(p.patient_key)}
                    slotProps={{
                      input: {
                        'aria-label': `Commit ${p.name || p.patient_key}`,
                      },
                    }}
                  />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {p.name || p.patient_key}
                    {chip && (
                      <Chip
                        size="small"
                        label={chip.label}
                        color={chip.color}
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {describeCounts(p.counts)}
                  </Typography>
                </Box>
                {resolvable && (
                  <Select
                    size="small"
                    displayEmpty
                    value={selectValue(p)}
                    onChange={e => handleSelect(p.patient_key, e.target.value)}
                    sx={{ minWidth: 220, maxWidth: 280 }}
                    inputProps={{
                      'aria-label': `Resolve ${p.name || p.patient_key}`,
                    }}
                  >
                    <MenuItem value="" disabled>
                      {status === 'ambiguous' ? 'Choose a match…' : 'Link to…'}
                    </MenuItem>
                    {(match?.candidates ?? []).map(c => (
                      <MenuItem key={c.patient_id} value={c.patient_id}>
                        {(c.name || c.patient_id) +
                          (c.dob ? ` · ${c.dob}` : '') +
                          ` · ${c.confidence}%`}
                      </MenuItem>
                    ))}
                    <MenuItem value={CREATE_NEW}>Create new patient</MenuItem>
                  </Select>
                )}
              </ListItem>
            );
          })}
          </List>
        </>
      )}
    </Box>
  );
}
